import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray } from 'react-hook-form';
import { MdAdd } from 'react-icons/md';
import styled from 'styled-components';

import { HeaderText, SectionContainer, SectionSubtitle } from '../../../components/Modals';
import { Flex } from '../../../components/ui/Flex';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../components/ui/Form';
import { SettingsModalInput } from '../../settings/SettingsModals';
import { Select, SelectItem } from '../../../components/ui/Select';
import ImageUploadWithPreviewV3 from '../../../components/ImageUploadWithPreviewV3';
import { UserRole } from '../../users/constant';
import { list } from '../../../utils/list';
import { useWatch } from 'react-hook-form';
import { LOCK_PROVIDERS } from '../../../utils/mappings';
import { MAX_CONTACTS_PER_BUILDING } from '../constants';
import BuildingContactForm from './BuildingContactForm';
import { ButtonBodyVariant } from '../../../components/ui/ButtonVariant';


const BuildingInfoFormInputs = ({ form, roleId, organizations, existingLogoUrl, isEdit }) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'contacts'
  });
  const selectedCountry = useWatch({
    control: form.control,
    name: 'country'
  });

  const addContact = () => {
    if (fields.length < MAX_CONTACTS_PER_BUILDING) {
      append({
        contact_name: '',
        contact_email: '',
        phone: ''
      });
    }
  };

  return (
    <SectionContainer>
      <HeaderText alt>{t('Building Information')}</HeaderText>
      <SectionSubtitle>
        {t('Provide the basic info and system preferences for this building.')}
      </SectionSubtitle>
      <Flex direction="column" gap="16px">
        {roleId === UserRole.SUPER_ADMIN && (
          <FormField
            control={form.control}
            name="org_id"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Organization')}</FormLabel>
                <FormControl>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    } 
                    placeholder={t('Select Organization')} 
                    {...field} 
                    disabled={isEdit}
                  >
                    {organizations.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Building Name')}</FormLabel>
              <FormControl row>
                <SettingsModalInput
                  type="text"
                  placeholder={t('Enter Name')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {roleId === UserRole.SUPER_ADMIN && (
          <FormField
            control={form.control}
            name="lock_provider"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Lock Brand')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Choose lock provider')} {...field}>
                    {LOCK_PROVIDERS.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Address')}</FormLabel>
              <FormControl row>
                <SettingsModalInput type="text" placeholder={'Enter Building Address'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Flex gap="24px">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('City')}</FormLabel>
                <FormControl row>
                  <SettingsModalInput type="text" placeholder={t('Enter City')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Province/State')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Select Location')} {...field}>
                    {list
                      .find((country) => {
                        return country.abbreviation === selectedCountry;
                      })
                      ?.states.map((state, index) => (
                        <SelectItem value={state.abbreviation} key={index + state.abbreviation}>
                          {state.name}
                        </SelectItem>
                      ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Flex>

        <Flex gap="24px">
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Country')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Select Country')} {...field}>
                    {list.map((item, index) => (
                      <SelectItem value={item.abbreviation} key={index + item.abbreviation}>
                        {item.country}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="zip"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Postal Code/ZIP')}</FormLabel>
                <FormControl row>
                  <SettingsModalInput type="text" placeholder={t('Enter Code')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Flex>

        <ImageUploadWithPreviewV3
          form={form} 
          existingImageUrl={existingLogoUrl} 
          fieldName="photo"
          instructionText={t('Drag and drop your building avatar here.')}
        />

        {/* Building Contacts */}
        <Flex direction="column" gap="8px">
          <Flex justify="space-between" alignItems="center">
            <div>
              <HeaderText alt style={{ marginBottom: '4px' }}>{t('Building Contacts')}</HeaderText>
              <SectionSubtitle>
                {`You can add up to ${MAX_CONTACTS_PER_BUILDING} contacts for this building.`}
              </SectionSubtitle>
            </div>
            <ButtonBodyVariant
              type="button"
              variant="ghost"
              onClick={addContact}
              disabled={fields.length >= MAX_CONTACTS_PER_BUILDING}
            >
              <MdAdd size={18} style={{ marginRight: '4px' }} />
              {t('Add Contact')}
            </ButtonBodyVariant>
          </Flex>

          {fields.length === 0 && (
            <NoContactsSection>
              {t('No contacts added yet. Click \"Add Contact\" to add one.')}
            </NoContactsSection>
          )}

          {fields.map((field, index) => (
            <BuildingContactForm
              key={field.id}
              form={form}
              index={index}
              onRemove={remove}
              canRemove={fields.length > 0}
            />
          ))}

          {fields.length > 0 && fields.length < MAX_CONTACTS_PER_BUILDING && (
            <ButtonBodyVariant
              type="button"
              variant="ghost"
              onClick={addContact}
              disabled={fields.length >= MAX_CONTACTS_PER_BUILDING}
            >
              <MdAdd size={18} style={{ marginRight: '4px' }} />
              {t('Add Contact')}
            </ButtonBodyVariant>
          )}
        </Flex>
      </Flex>
    </SectionContainer>
  );
};

export default BuildingInfoFormInputs;

const NoContactsSection = styled.div`
  padding: 16px;
  text-align: center;
  color: #8898aa;
  border: 1px dashed #d8dbe0;
  border-radius: 8px;
`;
